import { fetchApiJson } from "../config/apiBase";

const CACHE_PREFIX = "qtech_translation_cache_v2";
const NON_TRANSLATABLE_KEYS = new Set([
  "bg",
  "href",
  "to",
  "icon",
  "innerClass",
  "src",
  "image",
  "imageUrl",
  "videoUrl",
  "mapEmbedUrl",
]);

function shouldSkipByKey(key) {
  if (!key) return false;
  if (NON_TRANSLATABLE_KEYS.has(key)) return true;
  return key.toLowerCase().includes("url");
}

function looksLikeTechnicalValue(value) {
  const text = String(value || "").trim();
  if (!text) return true;
  if (/^https?:\/\//i.test(text)) return true;
  if (text.startsWith("/")) return true;
  if (/\.(png|jpe?g|gif|webp|svg|ico|mp4|webm)$/i.test(text)) return true;
  if (/^(flaticon|fa[sbrl]?\s)/i.test(text)) return true;
  return false;
}

function cacheKey(target) {
  return `${CACHE_PREFIX}:${target}`;
}

function readCache(target) {
  try {
    const raw = localStorage.getItem(cacheKey(target));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(target, data) {
  try {
    localStorage.setItem(cacheKey(target), JSON.stringify(data));
  } catch {
    // Ignore storage failures.
  }
}

function hasArabicChars(value) {
  return /[\u0600-\u06FF]/.test(String(value || ""));
}

async function translateWithGooglePublic(text, source = "en", target = "ar") {
  const params = new URLSearchParams({
    client: "gtx",
    dt: "t",
    sl: source,
    tl: target,
    q: text,
  });
  const response = await fetch(`https://translate.googleapis.com/translate_a/single?${params.toString()}`);
  if (!response.ok) throw new Error(`Google public translation failed: ${response.status}`);
  const payload = await response.json();
  const segments = Array.isArray(payload?.[0]) ? payload[0] : [];
  return segments.map((segment) => segment?.[0] || "").join("").trim() || text;
}

async function translateWithLibrePublic(text, source = "en", target = "ar") {
  const response = await fetch("https://libretranslate.de/translate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      q: text,
      source,
      target,
      format: "text",
    }),
  });
  if (!response.ok) throw new Error(`Libre public translation failed: ${response.status}`);
  const payload = await response.json();
  return String(payload?.translatedText || text);
}

async function translateWithMyMemory(text, source = "en", target = "ar") {
  const params = new URLSearchParams({
    q: text,
    langpair: `${source}|${target}`,
  });
  const response = await fetch(`https://api.mymemory.translated.net/get?${params.toString()}`);
  if (!response.ok) throw new Error(`MyMemory translation failed: ${response.status}`);
  const payload = await response.json();
  const translated = payload?.responseData?.translatedText;
  return String(translated || text);
}

export async function translateBatch(texts, target = "ar") {
  const normalized = texts.map((value) => String(value ?? ""));
  if (target === "en") return normalized;

  const cache = readCache(target);
  const missing = [];
  for (const text of normalized) {
    if (!text.trim()) continue;
    const cached = cache[text];
    if (!cached) {
      missing.push(text);
      continue;
    }
    if (target === "ar" && !hasArabicChars(cached) && cached === text) {
      missing.push(text);
    }
  }

  if (missing.length > 0) {
    const chunkSize = 40;
    const chunks = [];
    for (let i = 0; i < missing.length; i += chunkSize) {
      chunks.push(missing.slice(i, i + chunkSize));
    }

    try {
      for (const text of missing) {
        const candidate = await translateWithLibrePublic(text, "en", target);
        if (target === "ar") {
          if (hasArabicChars(candidate)) cache[text] = candidate;
        } else {
          cache[text] = candidate;
        }
      }
    } catch {
      try {
        for (const text of missing) {
          const candidate = await translateWithGooglePublic(text, "en", target);
          if (target === "ar") {
            if (hasArabicChars(candidate)) cache[text] = candidate;
          } else {
            cache[text] = candidate;
          }
        }
      } catch {
        try {
          for (const text of missing) {
            const candidate = await translateWithMyMemory(text, "en", target);
            if (target === "ar") {
              if (hasArabicChars(candidate)) cache[text] = candidate;
            } else {
              cache[text] = candidate;
            }
          }
        } catch {
          try {
            for (const chunk of chunks) {
              const { json } = await fetchApiJson("/api/i18n/translate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ source: "en", target, texts: chunk }),
              });

              const translated = Array.isArray(json?.translated) ? json.translated : [];
              chunk.forEach((text, index) => {
                const candidate = String(translated[index] ?? text);
                if (target === "ar") {
                  if (hasArabicChars(candidate)) cache[text] = candidate;
                } else {
                  cache[text] = candidate;
                }
              });
            }
          } catch {
            // Do not cache failures; allow retry next render.
          }
        }
      }
    }
    writeCache(target, cache);
  }

  return normalized.map((text) => cache[text] || text);
}

function collectStrings(value, output, parentKey = "") {
  if (typeof value === "string") {
    if (shouldSkipByKey(parentKey)) return;
    if (looksLikeTechnicalValue(value)) return;
    if (hasArabicChars(value)) return;
    output.push(value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectStrings(item, output, parentKey));
    return;
  }
  if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => collectStrings(item, output, key));
  }
}

function remapStrings(value, dictionary, parentKey = "") {
  if (typeof value === "string") {
    if (shouldSkipByKey(parentKey)) return value;
    if (looksLikeTechnicalValue(value)) return value;
    return dictionary.get(value) || value;
  }
  if (Array.isArray(value)) return value.map((item) => remapStrings(item, dictionary, parentKey));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, remapStrings(item, dictionary, key)])
    );
  }
  return value;
}

export function hydrateFromCacheDeep(value, target = "ar") {
  if (target === "en") return value;
  const cache = readCache(target);
  const dictionary = new Map(Object.entries(cache || {}));
  return remapStrings(value, dictionary);
}

export async function preloadTranslations(values, target = "ar") {
  if (target === "en") return values;
  const queue = Array.isArray(values) ? values : [values];
  await Promise.all(queue.map((value) => translateDeep(value, target)));
  return values;
}

export async function translateDeep(value, target = "ar") {
  if (target === "en") return value;
  const strings = [];
  collectStrings(value, strings);
  const unique = Array.from(new Set(strings.filter((item) => item.trim())));
  if (unique.length === 0) return value;

  const translated = await translateBatch(unique, target);
  const dictionary = new Map(unique.map((source, index) => [source, translated[index] || source]));
  return remapStrings(value, dictionary);
}
